import { fmtMoney } from '../utils/currency';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Receipt, Plus, DollarSign, Wallet, Coins, X } from 'lucide-react';
import {
  useToast,
  PageHeader, Button, Input, EmptyState, StatusBadge, FilterTabs,
} from '../components/ui';

const TYPE_META = {
  operational: { label: 'Operational', variant: 'neutral' },
  advance:     { label: 'Advance',     variant: 'warning' },
  purchase:    { label: 'Purchase',    variant: 'primary' },
  settlement:  { label: 'Settlement',  variant: 'success' },
};
const ADVANCE_LABEL = { petty_cash: 'Petty cash', salary: 'Salary advance' };
const SETTLE_LABEL = {
  petty_return: 'Cash returned',
  salary_recovery: 'Salary recovered',
  reimbursement: 'Reimbursed to staff',
};

const money = (n) => fmtMoney(n, true);

const EMPTY_FORM = {
  type: 'operational', amount: '', description: '', category: '',
  advanceType: 'petty_cash', settlementKind: 'petty_return',
  staff: '', vendor: '', fundedFromAdvance: false, note: '', date: '',
};

const selectCls =
  'w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500';

const Expenses = () => {
  const { toast } = useToast();
  const [expenses, setExpenses] = useState([]);
  const [balances, setBalances] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const headers = { headers: { 'x-auth-token': localStorage.getItem('token') } };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = () => { fetchExpenses(); fetchBalances(); fetchStaff(); };

  const fetchExpenses = async () => {
    try { setExpenses((await axios.get('/api/expenses', headers)).data); }
    catch (err) { console.error(err); }
  };
  const fetchBalances = async () => {
    try { setBalances((await axios.get('/api/expenses/balances', headers)).data); }
    catch (err) { console.error(err); }
  };
  const fetchStaff = async () => {
    try { setStaffList((await axios.get('/api/expenses/staff', headers)).data); }
    catch (err) { console.error(err); }
  };

  const openAdd = () => { setForm(EMPTY_FORM); setShowModal(true); };

  const openSettle = (staff, settlementKind, amount) => {
    setForm({
      ...EMPTY_FORM,
      type: 'settlement',
      settlementKind,
      staff: staff._id,
      amount: String(Math.abs(amount).toFixed(2)),
      description: `${SETTLE_LABEL[settlementKind]} — ${staff.name}`,
    });
    setShowModal(true);
  };

  const setField = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post('/api/expenses', form, headers);
      setShowModal(false);
      setForm(EMPTY_FORM);
      fetchAll();
      toast.success('Entry recorded');
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error saving entry');
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = filter === 'all' ? expenses : expenses.filter((e) => e.type === filter);

  // Summary figures
  const totalSpent = expenses
    .filter((e) => e.type === 'operational' || e.type === 'purchase')
    .reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const pettyOutstanding = balances.reduce((s, b) => s + Math.max(0, b.pettyHeld), 0);
  const salaryOutstanding = balances.reduce((s, b) => s + Math.max(0, b.salaryOwed), 0);

  const describe = (e) => {
    if (e.type === 'advance') return `${ADVANCE_LABEL[e.advanceType] || 'Advance'} to ${e.staff?.name || '—'}`;
    if (e.type === 'settlement') return `${SETTLE_LABEL[e.settlementKind] || 'Settlement'}${e.staff ? ` — ${e.staff.name}` : ''}`;
    if (e.type === 'purchase') return e.description || 'Purchase';
    return e.description || '—';
  };

  const STATS = [
    { label: 'Total spent', value: money(totalSpent), sub: 'Operational + purchases', Icon: DollarSign, tint: 'bg-warning-50 text-warning-600' },
    { label: 'Petty cash out', value: money(pettyOutstanding), sub: 'Floats held by staff', Icon: Wallet, tint: 'bg-primary-50 text-primary-600' },
    { label: 'Salary advances', value: money(salaryOutstanding), sub: 'Owed back by staff', Icon: Coins, tint: 'bg-danger-50 text-danger-600' },
  ];

  return (
    <>
      <PageHeader
        title="Expenses"
        subtitle="Operational costs, staff advances, outside purchases and settlements."
        action={<Button variant="primary" leftIcon={<Plus size={16} />} onClick={openAdd}>Add entry</Button>}
      />

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {STATS.map(({ label, value, sub, Icon, tint }) => (
          <div key={label} className="bg-white rounded-lg border border-neutral-200 shadow-card p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</p>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tint}`}><Icon size={18} /></div>
            </div>
            <p className="text-2xl font-bold text-neutral-900 tabular-nums">{value}</p>
            <p className="text-xs text-neutral-500 mt-1">{sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Expenses list */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden">
          <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-sm font-semibold text-neutral-800">Entries</h2>
            <FilterTabs
              value={filter}
              onChange={setFilter}
              options={['all', 'operational', 'advance', 'purchase', 'settlement']}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="app-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Details</th>
                  <th>Staff</th>
                  <th>Amount</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan="5">
                      <EmptyState
                        icon={Receipt}
                        title="No entries"
                        message="Use “Add entry” to record a bill, staff advance, purchase or settlement."
                      />
                    </td>
                  </tr>
                )}
                {filtered.map((e) => (
                  <tr key={e._id}>
                    <td>
                      <StatusBadge variant={TYPE_META[e.type]?.variant || 'neutral'} dot>
                        {TYPE_META[e.type]?.label || e.type}
                      </StatusBadge>
                    </td>
                    <td>
                      <div className="font-medium text-neutral-900">{describe(e)}</div>
                      <div className="text-xs text-neutral-500">
                        {e.type === 'operational' && e.category}
                        {e.type === 'purchase' && (
                          <>
                            {e.vendor ? `From ${e.vendor}` : 'No vendor'}
                            {e.fundedFromAdvance && ' · from float'}
                          </>
                        )}
                        {e.note}
                      </div>
                    </td>
                    <td className="text-sm text-neutral-700">{e.staff?.name || <span className="text-neutral-400">—</span>}</td>
                    <td className="font-semibold tabular-nums">{money(e.amount)}</td>
                    <td className="text-sm text-neutral-600">{e.date ? new Date(e.date).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Staff balances */}
        <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden h-fit">
          <div className="px-5 py-3 border-b border-neutral-200">
            <h2 className="text-sm font-semibold text-neutral-800">Staff balances</h2>
            <p className="text-xs text-neutral-500 mt-0.5">Who is holding cash or owes advances</p>
          </div>
          <div className="p-4 space-y-3">
            {balances.length === 0 && (
              <p className="text-sm text-neutral-500 text-center py-6">Everyone is settled up.</p>
            )}
            {balances.map((b) => (
              <div key={b.staff._id} className="border border-neutral-200 rounded-md p-3">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm text-neutral-900">{b.staff.name}</p>
                  <span className="text-[11px] text-neutral-500 capitalize">{b.staff.role?.replace('_', ' ')}</span>
                </div>
                <div className="mt-2 space-y-1.5">
                  {Math.abs(b.pettyHeld) >= 0.01 && (
                    <div className="flex items-center justify-between text-xs">
                      <span className={b.pettyHeld > 0 ? 'text-neutral-600' : 'text-danger-600'}>
                        {b.pettyHeld > 0 ? 'Holds float' : 'Overspent (owed back)'}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold tabular-nums">{money(Math.abs(b.pettyHeld))}</span>
                        <button
                          onClick={() => openSettle(
                            b.staff,
                            b.pettyHeld > 0 ? 'petty_return' : 'reimbursement',
                            b.pettyHeld,
                          )}
                          className="text-[11px] border border-neutral-300 rounded px-2 py-0.5 hover:bg-neutral-50"
                        >
                          {b.pettyHeld > 0 ? 'Return' : 'Reimburse'}
                        </button>
                      </div>
                    </div>
                  )}
                  {b.salaryOwed >= 0.01 && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-neutral-600">Salary advance</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold tabular-nums">{money(b.salaryOwed)}</span>
                        <button
                          onClick={() => openSettle(b.staff, 'salary_recovery', b.salaryOwed)}
                          className="text-[11px] border border-neutral-300 rounded px-2 py-0.5 hover:bg-neutral-50"
                        >
                          Recover
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add / settle modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-card-hover w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-neutral-900">Add entry</h2>
              <button type="button" onClick={() => setShowModal(false)} className="text-neutral-400 hover:text-neutral-700">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-4">
                {/* Type selector */}
                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-2">Entry type</label>
                  <FilterTabs
                    value={form.type}
                    onChange={(type) => setField({ type })}
                    options={[
                      { value: 'operational', label: 'Bill' },
                      { value: 'advance', label: 'Advance' },
                      { value: 'purchase', label: 'Purchase' },
                      { value: 'settlement', label: 'Settle' },
                    ]}
                  />
                </div>

                {/* Advance-specific */}
                {form.type === 'advance' && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-neutral-700 block mb-1">Advance type</label>
                      <select className={selectCls} value={form.advanceType} onChange={(e) => setField({ advanceType: e.target.value })}>
                        <option value="petty_cash">Petty cash (to buy things)</option>
                        <option value="salary">Salary advance (personal)</option>
                      </select>
                    </div>
                    <StaffSelect staffList={staffList} value={form.staff} onChange={(staff) => setField({ staff })} label="Given to" required />
                  </>
                )}

                {/* Purchase-specific */}
                {form.type === 'purchase' && (
                  <>
                    <Input label="Bought from (vendor)" type="text" placeholder="e.g. City Market"
                      value={form.vendor} onChange={(e) => setField({ vendor: e.target.value })} />
                    <label className="flex items-center gap-2 text-sm text-neutral-700">
                      <input type="checkbox" checked={form.fundedFromAdvance}
                        onChange={(e) => setField({ fundedFromAdvance: e.target.checked })}
                        className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-2 focus:ring-primary-200" />
                      Paid from a staff member’s petty-cash float
                    </label>
                    {form.fundedFromAdvance && (
                      <StaffSelect staffList={staffList} value={form.staff} onChange={(staff) => setField({ staff })} label="Whose float" required />
                    )}
                  </>
                )}

                {/* Settlement-specific */}
                {form.type === 'settlement' && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-neutral-700 block mb-1">What is being settled</label>
                      <select className={selectCls} value={form.settlementKind} onChange={(e) => setField({ settlementKind: e.target.value })}>
                        <option value="petty_return">Staff returned leftover cash</option>
                        <option value="reimbursement">Business reimbursed staff (overspend)</option>
                        <option value="salary_recovery">Salary advance recovered</option>
                      </select>
                    </div>
                    <StaffSelect staffList={staffList} value={form.staff} onChange={(staff) => setField({ staff })} label="Staff member" required />
                  </>
                )}

                {/* Operational category */}
                {form.type === 'operational' && (
                  <Input label="Category" type="text" placeholder="e.g. Utilities, Rent, Supplies"
                    value={form.category} onChange={(e) => setField({ category: e.target.value })} />
                )}

                {/* Common fields */}
                <Input label="Amount" type="number" step="0.01" placeholder="0.00"
                  value={form.amount} onChange={(e) => setField({ amount: e.target.value })} required />

                {form.type !== 'advance' && form.type !== 'settlement' && (
                  <Input label="Description" type="text" placeholder="What was this for?"
                    value={form.description} onChange={(e) => setField({ description: e.target.value })} />
                )}

                <Input label="Date (optional)" type="date"
                  value={form.date} onChange={(e) => setField({ date: e.target.value })} />

                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-1">Note (optional)</label>
                  <textarea rows="2" className={`${selectCls} resize-none`} value={form.note}
                    onChange={(e) => setField({ note: e.target.value })} />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-lg flex gap-2">
                <Button type="button" variant="secondary" fullWidth onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit" variant="primary" fullWidth loading={submitting}>Save entry</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

const StaffSelect = ({ staffList, value, onChange, label, required }) => (
  <div>
    <label className="text-sm font-medium text-neutral-700 block mb-1">{label}</label>
    <select
      className={selectCls}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
    >
      <option value="">Select staff…</option>
      {staffList.map((s) => (
        <option key={s._id} value={s._id}>{s.name}{s.role ? ` (${s.role.replace('_', ' ')})` : ''}</option>
      ))}
    </select>
  </div>
);

export default Expenses;
